import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { loginUser } from '../../store/slices/authSlice';
import { GoogleLogin } from '@react-oauth/google';
import api from '../../services/api';
import { storeSession, getDeviceId, getDeviceName } from '../../utils/sessionKeys';
import { useAnalytics } from '../../analytics/hooks';
import { getValidRedirect } from '../../utils/redirectUtils';

const Register = () => {
  const { register, loading, error: authError, setAuthUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const { track } = useAnalytics();

  const getPostRegisterPath = () => {
    const redirect = getValidRedirect(searchParams);
    return redirect || '/';
  };
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    dateOfBirth: '',
    timeOfBirth: '',
    placeOfBirth: '',
    gender: ''
  });
  const [formError, setFormError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.phone || !formData.password) {
      setFormError('Please fill in all required fields');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setFormError('Passwords do not match');
      return;
    }

    try {
      await register({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        dateOfBirth: formData.dateOfBirth ? new Date(formData.dateOfBirth) : null,
        timeOfBirth: formData.timeOfBirth ? new Date(`1970-01-01T${formData.timeOfBirth}`) : null,
        placeOfBirth: formData.placeOfBirth,
        gender: formData.gender
      });
      track('signup_completed', { method: 'email' });
      navigate(getPostRegisterPath(), { replace: true });
    } catch (err) {
      setFormError('Registration failed. Please try again.');
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      const response = await api.post('/auth/google', {
        credential: credentialResponse.credential,
      }, {
        headers: {
          'X-Device-ID': getDeviceId(),
          'X-Device-Name': getDeviceName(),
        }
      });

      const data = response.data;
      const regAccessToken = data.access_token || data.token;

      let enhancedUser = { ...data.user };
      try {
        const tokenParts = regAccessToken.split('.');
        if (tokenParts.length === 3) {
          const decodedPayload = JSON.parse(atob(tokenParts[1]));
          enhancedUser.role = decodedPayload.role;
        }
      } catch (e) { /* fallback */ }

      // Tokens are in httpOnly cookies. Store only user metadata.
      storeSession(enhancedUser.role || 'user', enhancedUser);

      dispatch(loginUser.fulfilled({
        user: enhancedUser,
      }, 'google-login', null));

      setAuthUser(enhancedUser, regAccessToken);
      track('signup_completed', { method: 'google' });
      navigate(getPostRegisterPath(), { replace: true });
    } catch (err) {
      setFormError(err.response?.data?.error || 'Google sign-up failed');
    }
  };

  return (
    <div className="auth-container">
      <h2>Sign Up</h2>
      <form onSubmit={handleSubmit} className="auth-form">
        <div className="form-group">
          <label htmlFor="name">Full Name *</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="email">Email *</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="phone">Phone Number *</label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="password">Password *</label>
          <input
            type="password"
            id="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            required
            minLength={6}
          />
        </div>
        <div className="form-group">
          <label htmlFor="confirmPassword">Confirm Password *</label>
          <input
            type="password"
            id="confirmPassword"
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleChange}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="dateOfBirth">Date of Birth</label>
          <input
            type="date"
            id="dateOfBirth"
            name="dateOfBirth"
            value={formData.dateOfBirth}
            onChange={handleChange}
          />
        </div>
        <div className="form-group">
          <label htmlFor="timeOfBirth">Time of Birth</label>
          <input
            type="time"
            id="timeOfBirth"
            name="timeOfBirth"
            value={formData.timeOfBirth}
            onChange={handleChange}
          />
        </div>
        <div className="form-group">
          <label htmlFor="placeOfBirth">Place of Birth</label>
          <input
            type="text"
            id="placeOfBirth"
            name="placeOfBirth"
            value={formData.placeOfBirth}
            onChange={handleChange}
          />
        </div>
        <div className="form-group">
          <label htmlFor="gender">Gender</label>
          <select
            id="gender"
            name="gender"
            value={formData.gender}
            onChange={handleChange}
          >
            <option value="">Select Gender</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other</option>
          </select>
        </div>
        {formError && <div className="error-message">{formError}</div>}
        {authError && <div className="error-message">{authError}</div>}
        <button type="submit" disabled={loading} className="auth-button">
          {loading ? 'Signing up...' : 'Sign Up'}
        </button>
      </form>

      <div className="auth-divider">
        <span>OR</span>
      </div>

      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <GoogleLogin
          onSuccess={handleGoogleSuccess}
          onError={() => setFormError('Google sign-up failed. Please try again.')}
          theme="outline"
          size="large"
          width="350"
          text="signup_with"
        />
      </div>

      <p className="auth-switch">
        Already have an account? <a href="/login">Log in here</a>
      </p>
    </div>
  );
};

export default Register;
